/**
 * -------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation.  All Rights Reserved.  Licensed under the MIT License.
 * See License in the project root for license information.
 * -------------------------------------------------------------------------------------------
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { LitElement } from 'lit-element';
import { Providers } from '../Providers';
/**
 * Defines media query based on component width
 *
 * @export
 * @enum {string}
 */
export var ComponentMediaQuery;
(function (ComponentMediaQuery) {
    /**
     * devices with width < 768
     */
    ComponentMediaQuery["mobile"] = "";
    /**
     * devies with width < 1200
     */
    ComponentMediaQuery["tablet"] = "tablet";
    /**
     * devices with width > 1200
     */
    ComponentMediaQuery["desktop"] = "desktop";
})(ComponentMediaQuery || (ComponentMediaQuery = {}));
/**
 * BaseComponent extends LitElement including ShadowRoot toggle and fireCustomEvent features
 *
 * @export  MgtBaseComponent
 * @abstract
 * @class MgtBaseComponent
 * @extends {LitElement}
 */
export class MgtBaseComponent extends LitElement {
    constructor() {
        super();
        /**
         * determines if login component is in loading state
         * @type {boolean}
         */
        this._isLoadingState = false;
        this._isFirstUpdated = false;
        if (this.isShadowRootDisabled()) {
            this._needsShimAdoptedStyleSheets = true;
        }
    }
    /**
     * Get ShadowRoot toggle, returns value of _useShadowRoot
     *
     * @static _useShadowRoot
     * @memberof MgtBaseComponent
     */
    static get useShadowRoot() {
        return this._useShadowRoot;
    }
    /**
     * Set ShadowRoot toggle value
     *
     * @static _useShadowRoot
     * @memberof MgtBaseComponent
     */
    static set useShadowRoot(value) {
        this._useShadowRoot = value;
    }
    /**
     * Gets the ComponentMediaQuery of the component
     *
     * @readonly
     * @type {ComponentMediaQuery}
     * @memberof MgtBaseComponent
     */
    get mediaQuery() {
        if (this.offsetWidth < 768) {
            return ComponentMediaQuery.mobile;
        }
        else if (this.offsetWidth < 1200) {
            return ComponentMediaQuery.tablet;
        }
        else {
            return ComponentMediaQuery.desktop;
        }
    }
    /**
     * A flag to check if the component is loading data state.
     *
     * @protected
     * @memberof MgtBaseComponent
     */
    get isLoadingState() {
        return this._isLoadingState;
    }
    /**
     * A flag to check if the component has updated once.
     *
     * @readonly
     * @protected
     * @type {boolean}
     * @memberof MgtBaseComponent
     */
    get isFirstUpdated() {
        return this._isFirstUpdated;
    }
    /**
     * Receive ShadowRoot Disabled value
     *
     * @returns boolean _useShadowRoot value
     * @memberof MgtBaseComponent
     */
    isShadowRootDisabled() {
        return !MgtBaseComponent._useShadowRoot || !this.constructor._useShadowRoot;
    }
    /**
     * Invoked when the element is first updated. Implement to perform one time
     * work on the element after update.
     *
     * Setting properties inside this method will trigger the element to update
     * again after this update cycle completes.
     *
     * @param _changedProperties Map of changed properties with old values
     */
    firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        this._isFirstUpdated = true;
        Providers.onProviderUpdated(() => this.requestStateUpdate());
        this.requestStateUpdate();
    }
    /**
     * load state into the component.
     * Override this function to provide additional loading logic.
     */
    loadState() {
        return Promise.resolve();
    }
    /**
     * helps facilitate creation of events across components
     *
     * @protected
     * @param {string} eventName name given to specific event
     * @param {*} [detail] optional any value to dispatch with event
     * @returns {boolean}
     * @memberof MgtBaseComponent
     */
    fireCustomEvent(eventName, detail) {
        const event = new CustomEvent(eventName, {
            bubbles: false,
            cancelable: true,
            detail
        });
        return this.dispatchEvent(event);
    }
    /**
     * method to create ShadowRoot if disabled flag isn't present
     *
     * @protected
     * @returns boolean
     * @memberof MgtBaseComponent
     */
    createRenderRoot() {
        return this.isShadowRootDisabled() ? this : super.createRenderRoot();
    }
    /**
     * Invoked whenever the element is updated. Implement to perform
     * post-updating tasks via DOM APIs, for example, focusing an element.
     *
     * Setting properties inside this method will trigger the element to update
     * again after this update cycle completes.
     *
     * * @param changedProperties Map of changed properties with old values
     */
    updated(changedProperties) {
        super.updated(changedProperties);
        const event = new CustomEvent('updated', {
            bubbles: true,
            cancelable: true
        });
        this.dispatchEvent(event);
    }
    /**
     * Request to reload the state.
     * Use reload instead of load to ensure loading events are fired.
     *
     * @protected
     * @memberof MgtBaseComponent
     */
    requestStateUpdate(force = false) {
        return __awaiter(this, void 0, void 0, function* () {
            // the component is still bootstraping - wait until first updated
            if (!this._isFirstUpdated) {
                return;
            }
            // Wait for the current load promise to complete (unless forced).
            if (this.isLoadingState && !force) {
                yield this._currentLoadStatePromise;
            }
            const loadStatePromise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                try {
                    this.setLoadingState(true);
                    this.fireCustomEvent('loadingInitiated');
                    yield this.loadState();
                    this.setLoadingState(false);
                    this.fireCustomEvent('loadingCompleted');
                    resolve();
                }
                catch (e) {
                    this.setLoadingState(false);
                    this.fireCustomEvent('loadingFailed');
                    reject(e);
                }
            }));
            // Return the load state promise.
            // If loading + forced, chain the promises.
            return (this._currentLoadStatePromise =
                this.isLoadingState && !!this._currentLoadStatePromise && force
                    ? this._currentLoadStatePromise.then(() => loadStatePromise)
                    : loadStatePromise);
        });
    }
    setLoadingState(value) {
        if (this._isLoadingState === value) {
            return;
        }
        this._isLoadingState = value;
        this.requestUpdate('isLoadingState');
    }
}
MgtBaseComponent._useShadowRoot = true;
//# sourceMappingURL=baseComponent.js.map